const en = Object.freeze({
  brand: Object.freeze({
    subtitle: "Chinese Cultural Edition",
  }),

  nav: Object.freeze({
    home: "Home",
    form: "Profile",
  }),

  language: Object.freeze({
    zh: "中文",
    en: "EN",
  }),

  landing: Object.freeze({
    eyebrow: "Life Blueprint · Chinese Cultural Edition",
    heroTitlePrefix: "Understand your ",
    heroTitleHighlight1: "patterns",
    heroTitleMiddle: "Build your ",
    heroTitleHighlight2: "life plan with clarity",
    heroSubtitle: "Personal Life Blueprint Report",
    heroDescriptionLine1: "Combining traditional Chinese analytical frameworks with practical personal planning,",
    heroDescriptionLine2: "Life Blueprint turns structured insights into clear priorities and actionable next steps.",
    start: "Start My Life Blueprint",
    viewReport: "View My Report",
    disclaimer: "This system is based on traditional cultural frameworks and personal planning concepts. It is provided for reference only and does not constitute financial, medical, legal, or guaranteed outcome advice.",
    reportSectionsLabel: "Report Sections",
    section1: "Core Profile",
    section2: "Key Indices",
    section3: "Career & Roles",
    section4: "Personal Strategy",
    section5: "Annual Guidance",
    section6: "Directional Guidance",
    section7: "Number Guidance",
    section8: "90-Day Action Plan",
    section9: "Wealth Action Blueprint",
    reportSectionsDescription: "Nine structured analysis sections form your complete Life Blueprint personal report.",

    commercialEyebrow: "Life Blueprint Products",
    commercialTitle: "A Structured Blueprint for Practical Life Planning",
    commercialDescription: "Start with the Personal Life Blueprint Report. AI-Guided Interpretation will be introduced as an optional explanation layer in a future release.",

    reportProductLabel: "Core Product",
    reportProductTitle: "Personal Life Blueprint Report",
    reportProductStatus: "Available",
    reportProductDescription: "A personalised report that organises your Life Blueprint into nine structured sections for clearer planning and action.",
    reportBenefit1: "Personalised Life Blueprint",
    reportBenefit2: "9 structured report sections",
    reportBenefit3: "Career and role direction",
    reportBenefit4: "Personal strategy and annual priorities",
    reportBenefit5: "Direction and number guidance",
    reportBenefit6: "90-Day Action Plan and Wealth Action Blueprint",
    reportBenefit7: "Available in Chinese or English",
    reportProductCta: "Create My Life Blueprint",

    aiProductLabel: "Optional Interpretation Layer",
    aiProductTitle: "AI-Guided Interpretation",
    aiProductStatus: "Coming Soon",
    aiProductDescription: "Designed to help you understand an already-generated Life Blueprint report and turn its findings into practical next steps.",
    aiBenefit1: "Explain and clarify generated report findings",
    aiBenefit2: "Highlight priorities based on your approved report",
    aiBenefit3: "Break the 90-Day Action Plan into practical next steps",
    aiProductBoundary: "AI-Guided Interpretation will explain the generated report only. It will not recalculate, replace, or contradict the official Life Blueprint results.",

    feature1Title: "Core Profile & Key Indices",
    feature1Desc: "Understand your core profile, key indices and career alignment to clarify your current priorities.",
    feature2Title: "Strategy & Annual Planning",
    feature2Desc: "Use personal strategy, annual guidance and directional insights to plan with greater clarity.",
    feature3Title: "Numbers & Wealth Action",
    feature3Desc: "Turn number guidance, your 90-day action plan and wealth blueprint into practical next steps.",
    footerBrand: "© Life Blueprint",
    footerNote: "For personal planning reference only",
  }),

  form: Object.freeze({
    eyebrow: "Step 01 · Personal Profile",
    title: "Enter Your Personal Information",
    introBeforeBrand: "The information below is used to generate your ",
    introAfterBrand: " personal report. No account registration is required, and your data is stored only in your browser.",

    samplePicker: "Quick Start: Choose a Sample Profile",
    sampleA: "Sample A · 张明",
    sampleB: "Sample B · 李雨欣",
    sampleC: "Sample C · 陈家豪",

    name: "Name",
    namePlaceholder: "Example: Zhang Ming",
    birthDate: "Date of Birth",
    birthTime: "Birth Hour (Reference)",
    birthTimePlaceholder: "Select birth hour (reference only)",
    birthTimeExact: "Exact Birth Time (Required)",
    birthTimeExactHint: "An exact HH:MM time is required for an official V2.9.9 report. The birth-hour selection above is for reference only and is not used in the authoritative calculation.",

    currentGoal: "Current Goal",
    currentGoalPlaceholder: "Select your current goal",
    referenceYear: "Reference Year",
    referenceYearPlaceholder: "Enter the reference year",
    productionFieldsHint: "These fields are used to generate your Life Blueprint report. If required information is missing, the system will not substitute the current year or a default goal.",

    gender: "Gender",
    genderMale: "Male",
    genderFemale: "Female",

    region: "Current Country / Region",
    regionPlaceholder: "Select your current country / region",

    disclaimer: "This report is generated using traditional cultural frameworks and is provided for reference only. It does not constitute financial, medical, legal, or guaranteed outcome advice.",

    submit: "Generate My Life Blueprint",
    reset: "Clear",

    missingTitle: "Please complete the required information",
    missingProduction: "Please provide your name, date of birth, exact birth time (HH:MM), gender, current country / region, current goal, and reference year.",
    missingMock: "Please complete all required fields before generating your Life Blueprint.",
    persistenceTitle: "Report Could Not Be Saved",
    persistenceDescription: "The analysis was completed, but the result could not be fully saved in your browser. Please check browser storage permissions or available space and try again.",
    engineErrorTitle: "Report Generation Failed",
    engineErrorPrefix: "The report could not be generated: ",
    unknownError: "Unknown error",
    successTitle: "Your Blueprint Is Ready",
    successDescription: "Opening your personal overview…",
    sampleAppliedTitle: "Sample Profile Loaded",
    sampleAppliedDescription: "Select “Generate My Life Blueprint” to view the personalized result.",

    birthZi: "Zi Hour (23:00-01:00)",
    birthChou: "Chou Hour (01:00-03:00)",
    birthYin: "Yin Hour (03:00-05:00)",
    birthMao: "Mao Hour (05:00-07:00)",
    birthChen: "Chen Hour (07:00-09:00)",
    birthSi: "Si Hour (09:00-11:00)",
    birthWu: "Wu Hour (11:00-13:00)",
    birthWei: "Wei Hour (13:00-15:00)",
    birthShen: "Shen Hour (15:00-17:00)",
    birthYou: "You Hour (17:00-19:00)",
    birthXu: "Xu Hour (19:00-21:00)",
    birthHai: "Hai Hour (21:00-23:00)",
    birthUnknown: "Not Sure / Unknown",

    regionMalaysia: "Malaysia",
    regionSingapore: "Singapore",
    regionMainlandChina: "Mainland China",
    regionHongKong: "Hong Kong SAR",
    regionMacao: "Macao SAR",
    regionTaiwan: "Taiwan",
    regionOtherAsia: "Other Asia",
    regionEurope: "Europe",
    regionNorthAmerica: "North America",
    regionOceania: "Oceania",
    regionMiddleEast: "Middle East",
    regionOther: "Other",

    // Legacy labels retained for backward-compatible rendering.
    regionEastChina: "East China",
    regionNorthChina: "North China",
    regionSouthChina: "South China",
    regionCentralChina: "Central China",
    regionSouthwestChina: "Southwest China",
    regionNorthwestChina: "Northwest China",
    regionNortheastChina: "Northeast China",
    regionHmt: "Hong Kong / Macao / Taiwan",
    regionOverseas: "Overseas",

    goalComprehensive: "Comprehensive Analysis",
    goalCareer: "Career Development",
    goalWealth: "Wealth Growth",
    goalEntrepreneurship: "Entrepreneurship",
    goalLearning: "Learning & Development",
    goalCollaboration: "Mentors & Collaboration",
    goalStability: "Stable Living",
    goalBrand: "Personal Brand",
    goalSales: "Sales & Communication",
    goalContent: "Content Creation",
    goalLeadership: "Management & Leadership",
    goalClientRelations: "Client Relationships",
  }),


  commercial: Object.freeze({
    eyebrow: "Secure Checkout",
    title: "Unlock Your Life Blueprint Report",
    description: "Create a secure checkout, complete payment in Billplz, and return here while we verify the payment with the server.",
    name: "Customer Name",
    email: "Email Address",
    creating: "Creating Checkout…",
    createCheckout: "Continue to Secure Payment",
    awaitingPayment: "Payment Pending",
    checkingStatus: "Checking payment status…",
    openPayment: "Open Billplz Payment",
    checkAgain: "Check Payment Again",
    checkoutEnded: "Checkout Ended",
    startNewCheckout: "Start New Checkout",
    ambiguousWarning: "The payment provider outcome is currently uncertain. Do not start another payment yet. Please check again later or contact support.",
    paymentConfirmed: "Payment Confirmed",
    entitlementActive: "Your paid report access is active. You may now continue to your profile.",
    continueToProfile: "Continue to Profile",
    checkingAccess: "Verifying paid access…",
    createError: "Unable to create checkout. Please try again.",
    statusError: "Unable to verify payment status. Please try again.",
    authorityNote: "Access is granted only after the server confirms PAID status and an ACTIVE entitlement. A payment-page redirect alone does not unlock the report.",
  }),

  dashboard: Object.freeze({
    loading: "Loading…",

    eyebrow: "Life Blueprint · Personal Overview",
    title: "My Life Blueprint",
    profileOverview: "Personal Overview",
    sampleUser: "Sample User",
    profileLabel: "Profile",

    reportReady: "Report Ready",
    reportUnavailable: "Report Unavailable",
    sampleResult: "Sample Result",
    editProfile: "Edit Profile",
    downloadPdf: "Download PDF",
    pdfPreparing: "Preparing PDF…",
    pdfDownloadFailed: "PDF download failed. Please try again.",

    defaultNoticeBefore: "You are currently viewing the default sample profile. Go to the",
    defaultNoticeLink: "profile form",
    defaultNoticeAfter: "to enter your information and view your personalized blueprint.",

    summaryTitle: "Overview Summary",
    overviewTitle: "Life Blueprint Overview",
    overviewNote: "This chart provides an illustrative overview across six dimensions to highlight current areas of focus. Final weighting and calculations are determined by the formal Life Blueprint engine.",

    themesTitle: "12 Theme Analysis",
    strength: "Strength",

    numbersTitle: "24 Number Guidance",
    favorableNumbers: "Favorable Numbers",
    cautionaryNumbers: "Cautionary Numbers",
    usage: "Usage Guidance",
    numbersNote: "Number guidance in sample mode is for reference only and should not be used as the sole basis for a decision.",

    wealthTitle: "Personal Wealth Action Blueprint",
    wealthFocus: "Current Focus",
    wealthPriority: "Priority Actions",
    wealthWatch: "What to Watch",
    wealthNext: "Next Steps",

    failclosedTitle: "Report Unavailable",
    failclosedMessage: "A valid production report could not be found. Please re-enter your information and generate your Life Blueprint again.",
    failclosedButton: "Go to Profile Form",

    footerBrand: "© Life Blueprint",
    footerBrandSample: "© Life Blueprint · Sample Mode",
    footerProductionNote: "Traditional cultural and personal planning reference · not financial, medical or legal advice",
    footerSampleNote: "Sample results · not financial, medical or legal advice",

    themeDetail: "Theme Detail",
    energyStrength: "Energy Strength",
    themeSampleNote: "This theme analysis is a sample result. The formal report follows the Life Blueprint production rules.",
  }),


  report: Object.freeze({
    profileTitle: "Profile",
    name: "Name",
    birthDate: "Date of Birth",
    gender: "Gender",
    region: "Current Country / Region",
    exactBirthTime: "Exact Birth Time",

    planningContext: "Planning Context",
    currentGoal: "Current Goal",
    referenceYear: "Reference Year",

    sectionCore: "Core Profile",
    sectionIndices: "Key Indices",
    sectionCareer: "Career & Roles",
    sectionStrategy: "Personal Strategy",
    sectionAnnual: "Annual Guidance",
    sectionDirection: "Directional Guidance",
    sectionNumber: "Number Guidance",
    sectionActionPlan: "90-Day Action Plan",
    sectionWealth: "Wealth Action Blueprint",

    fourPillars: "Four Pillars",
    dayMaster: "Day Master",
    dayMasterStrength: "Day Master Strength",
    strengthScore: "Strength Score",
    neededElement: "Needed Element",
    seasonalSupport: "Seasonal Support",
    rootSupport: "Root Support",
    peerSupport: "Peer Support",

    indexCareerFit: "Career Alignment",
    indexWealth: "Wealth Development",
    indexVenture: "Entrepreneurial Fit",
    indexLearning: "Learning & Growth",
    indexNetwork: "Mentor & Collaboration",
    indexStability: "Life Stability",

    topIndustries: "Top Industries",
    topRoles: "Top Roles",

    strategyCorePosition: "Core Positioning",
    strategyCorePositionExplanation: "Positioning Explanation",
    strategyCareerStrategy: "Career Strategy",
    strategyCareerAction: "Career Action",
    strategyWealthStrategy: "Wealth Strategy",
    strategyWealthAction: "Wealth Action",
    strategyVentureFeasibility: "Entrepreneurial Feasibility",
    strategyLearningGrowth: "Learning & Growth",
    strategyMentorCooperation: "Mentor & Collaboration",
    strategyStableLife: "Life Stability",
    strategyAnnualFocus: "Annual Focus",
    strategyRiskAlert: "Risk Alert",
    strategyResponseAdvice: "Response Guidance",
    strategyAvoidItems: "What to Avoid",
    strategySummaryAdvice: "Overall Guidance",

    annualTitle: "Annual Theme",
    annualSuitableActions: "Suitable Actions",
    annualActionsToAvoid: "Actions to Avoid",
    annualCareerAdvice: "Career Guidance",
    annualWealthRhythm: "Wealth Rhythm",
    annualLearningAdvice: "Learning Guidance",
    annualRelationshipAdvice: "Relationship Guidance",
    annualVentureCareerAdvice: "Entrepreneurship & Career Guidance",
    annualActionList: "Annual Action List",
    annualWeaknessDescription: "Development Area",
    annualElementCareerAdvice: "Five Elements Career Guidance",
    annualElementWealthAdvice: "Five Elements Wealth Guidance",

    directionTopDevelopment: "Primary Development Direction",
    directionSecondaryDevelopment: "Secondary Development Direction",
    directionReduceDevelopment: "Direction to Reduce",
    directionPrimary: "Primary Direction",
    directionSecondary: "Secondary Direction",
    directionReduce: "Reduced Direction",
    directionRecommendedIndustries: "Recommended Industries",
    directionRecommendedRoles: "Recommended Roles",
    directionSuitableEnvironment: "Suitable Environment",
    directionUnsuitableEnvironment: "Less Suitable Environment",
    directionCareerAdvice: "Career Guidance",
    directionWealthAdvice: "Wealth Guidance",
    directionVentureAdvice: "Entrepreneurship Guidance",
    directionParallelWarning: "Tie Notice",
    directionIntegratedConclusion: "Integrated Conclusion",

    numberCoreBasis: "Core Basis",
    numberElementMeaning: "Five Elements Meaning",
    numberLifeSummary: "Life Number",
    numberLifeNumberRaw: "Life Number Value",
    numberLifeElement: "Life Number Element",
    numberRuleDigest: "Rule Summary",
    numberOverallRelation: "Overall Relationship",
    numberBoundaryHint: "Boundary Note",
    numberCoreBlock: "Core Numbers",
    numberAuxBlock: "Supporting Numbers",
    numberLifeBlock: "Life Number Set",
    numberCombinationStructure: "Combination Structure",
    numberPriorityOrder: "Priority Order",
    numberReinforcementLogic: "Reinforcement Logic",
    numberLifeUsage: "Life Number Usage",
    numberReduceBlock: "Numbers to Use Less",
    numberReduceRelation: "Reduced Relationship",
    numberLifeOverlap: "Life Number Overlap",
    numberReduceBoundary: "Reduction Boundary",
    numberMobileAdvice: "Mobile Number Guidance",
    numberVehicleAdvice: "Vehicle Plate Guidance",
    numberDoorAdvice: "Address Number Guidance",
    numberAccountAdvice: "Account Number Guidance",
    numberSummaryUsage: "Usage Summary",
    numberIntegratedRecommendation: "Integrated Guidance",

    personalYear: "Personal Year",
    actionCurrentGoal: "Current Goal",
    phase30: "First 30 Days",
    phase60: "Days 31–60",
    phase90: "Days 61–90",
    dayMasterNote: "Day Master Note",
    annualRiskNote: "Annual Risk Note",
    actionPlanSourceUnavailable: "No goal-specific action is available for “Comprehensive Analysis” in the current validated source.",

    wealthPersonality: "Wealth Profile",
    wealthIncomeTendency: "Income Tendency",
    wealthIncomeStructureAdvice: "Income Structure Guidance",
    wealthStrongestSummary: "Strongest Capability",
    wealthPriorityReinforcement: "Priority Development Area",
    wealthStageConclusion: "Stage Conclusion",
    wealthRecommendedModeDetail: "Recommended Mode",
    wealthAnnualRhythmDetail: "Annual Rhythm",
    wealthGuiRenResource: "Mentor & Support Resources",
    wealthRiskToleranceDetail: "Risk Tolerance",
    wealthDay30Plan: "30-Day Plan",
    wealthDay60Plan: "60-Day Plan",
    wealthDay90Plan: "90-Day Plan",
    wealthNumberCulture: "Number Culture Reference",
    wealthIntegratedPriority: "Integrated Priority",

    editProfile: "Edit Profile",
  }),

});

export default en;
